/**
 * Composant de sélection des feuilles journalières pour une prestation
 * Affiche la liste des jours avec leur statut de feuille
 */

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { API_ENDPOINTS } from '../../utils/apiConfig';
import DailyWorksheet from './DailyWorksheet';
import { toLocalDateStr } from '../../utils/dateUtils';
import './WorksheetSelector.css';

const STATUS_INFO = {
  draft: { label: 'Brouillon', color: '#9E9E9E', icon: '📝' },
  submitted: { label: 'Soumis', color: '#FF9800', icon: '⏳' },
  validated: { label: 'Validé', color: '#4CAF50', icon: '✅' },
  rejected: { label: 'Rejeté', color: '#F44336', icon: '❌' },
  none: { label: 'Non créé', color: '#E0E0E0', icon: '➕' }
};

const WorksheetSelector = ({
  prestationId,
  prestationNumber,
  startDate,
  endDate,
  onClose
}) => {
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState([]);
  const [worksheets, setWorksheets] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);

  // Générer la liste des jours entre startDate et endDate
  const generateDays = () => {
    const result = [];
    const start = new Date(startDate);
    const end = new Date(endDate);

    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      result.push(toLocalDateStr(new Date(d)));
    }

    return result;
  };

  // Charger les feuilles existantes
  const loadWorksheets = async () => {
    // Protection : ne pas appeler l'API si prestationId est manquant
    if (!prestationId) {
      console.error('WorksheetSelector: prestationId manquant');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const response = await fetch(
        API_ENDPOINTS.WORKSHEET_BY_PRESTATION(prestationId),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const data = await response.json();

      if (data.success) {
        setWorksheets(data.data || []);
      }

      setDays(generateDays());
    } catch (error) {
      console.error('Erreur chargement worksheets:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWorksheets();
  }, [prestationId]);

  // Obtenir le statut d'une feuille pour un jour donné
  const getWorksheetStatus = (date) => {
    const worksheet = worksheets.find(w => {
      // Normaliser les dates pour la comparaison (format YYYY-MM-DD)
      // Utiliser toLocaleDateString pour éviter les problèmes de fuseau horaire
      const wsDate = new Date(w.work_date).toLocaleDateString('sv-SE'); // Format ISO YYYY-MM-DD
      return wsDate === date;
    });
    return worksheet ? worksheet.status_code : 'none';
  };

  // Formater la date
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };

  // Ouvrir une feuille pour un jour
  const handleSelectDay = (date) => {
    setSelectedDay(date);
  };

  // Fermer la feuille et recharger
  const handleCloseWorksheet = () => {
    setSelectedDay(null);
    loadWorksheets();
  };

  // Utiliser un Portal pour afficher la feuille en plein écran, en dehors de la modale parent
  if (selectedDay) {
    return ReactDOM.createPortal(
      <div className="worksheet-fullscreen-overlay">
        <div className="worksheet-fullscreen-container">
          <DailyWorksheet
            prestationId={prestationId}
            workDate={selectedDay}
            onClose={handleCloseWorksheet}
            onSaved={handleCloseWorksheet}
          />
        </div>
      </div>,
      document.body
    );
  }

  return (
    <div className="worksheet-selector">
      <div className="selector-header">
        <h3>Feuilles Journalières</h3>
        <span className="prestation-ref">{prestationNumber}</span>
        <button className="close-btn" onClick={onClose}>&times;</button>
      </div>

      <div className="selector-info">
        <p>Sélectionnez un jour pour saisir ou consulter la feuille journalière de travail.</p>
      </div>

      {loading ? (
        <div className="loading">Chargement...</div>
      ) : (
        <div className="days-list">
          {days.map((date, index) => {
            const status = getWorksheetStatus(date);
            const statusInfo = STATUS_INFO[status];

            return (
              <div
                key={date}
                className={`day-item status-${status}`}
                onClick={() => handleSelectDay(date)}
              >
                <div className="day-number">J{index + 1}</div>
                <div className="day-date">{formatDate(date)}</div>
                <div
                  className="day-status"
                  style={{ backgroundColor: statusInfo.color }}
                >
                  <span className="status-icon">{statusInfo.icon}</span>
                  <span className="status-label">{statusInfo.label}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="selector-legend">
        <h4>Légende</h4>
        <div className="legend-items">
          {Object.entries(STATUS_INFO).map(([key, info]) => (
            <div key={key} className="legend-item">
              <span
                className="legend-color"
                style={{ backgroundColor: info.color }}
              />
              <span className="legend-label">{info.icon} {info.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WorksheetSelector;
